import AdminTable from '../../../components/admin/AdminTable';
﻿// pages/ConfigCorrespondenciaPage.tsx
import Loading from '../../../components/Loading';
import React, { useState, useEffect, useCallback } from 'react';
import { Edit, Trash2, History, Plus, Notebook } from 'lucide-react';
import {
    fetchCorrespondenciaConfigs,
    CorrespondenciaConfig,
    toggleCorrespondenciaConfigStatus
} from '../../../services/correspondenciaConfigService';
import AddCorrespondencia from '../../../components/config-correspondencia/AddCorrespondencia';
import EditCorrespondencia from '../../../components/config-correspondencia/EditCorrespondencia';
import DeleteCorrespondencia from '../../../components/config-correspondencia/DeleteCorrespondencia';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const ConfigCorrespondenciaPage: React.FC = () => {
    const [configs, setConfigs] = useState<CorrespondenciaConfig[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<CorrespondenciaConfig | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('correspondenciaconfig', userLevelId);

    const loadConfigs = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            if (perm?.can_read) {
                const data = await fetchCorrespondenciaConfigs();
                setConfigs(data);
            } else {
                setConfigs([]);
            }
        } catch (err) {
            setError('Error al cargar las configuraciones');
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm]);

    useEffect(() => {
        if (perm !== null) {
            loadConfigs();
        }
    }, [loadConfigs, perm]);

    const handleRefresh = () => {
        loadConfigs();
    };

    const handleConfigCreated = (newConfig: CorrespondenciaConfig) => {
        setConfigs(prev => [...prev, newConfig]);
    };

    const handleConfigUpdated = (updatedConfig: CorrespondenciaConfig) => {
        setConfigs(prev => prev.map(config =>
            config.id === updatedConfig.id ? updatedConfig : config
        ));
    };

    const handleConfigDeleted = (deletedConfigId: number) => {
        setConfigs(prev => prev.filter(config => config.id !== deletedConfigId));
    };

    const openEditModal = (config: CorrespondenciaConfig) => {
        setSelectedConfig(config);
        setIsEditModalOpen(true);
    };

    const openDeleteModal = (config: CorrespondenciaConfig) => {
        setSelectedConfig(config);
        setIsDeleteModalOpen(true);
    };

    const handleToggleStatus = async (id: number, currentStatus: boolean) => {
        try {
            await toggleCorrespondenciaConfigStatus(id, !currentStatus);
            await loadConfigs();
        } catch (err) {
            /* Browser diagnostics omitted. */
            alert('Error al cambiar el estado');
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    // Función para obtener el nombre completo del servicio y plan
    const getPlanServicioCompleto = (config: CorrespondenciaConfig) => {
        if (config.precio_plan_servicio_info) {
            return `${config.precio_plan_servicio_info.nombre_servicio} - ${config.precio_plan_servicio_info.plan_nombre}`;
        }
        return 'N/A - N/A';
    };

    // Función para obtener el nombre para el modal de eliminar
    const getNombreCompletoParaModal = (config: CorrespondenciaConfig) => {
        if (config.precio_plan_servicio_info) {
            return `${config.precio_plan_servicio_info.nombre_servicio} - ${config.precio_plan_servicio_info.plan_nombre}`;
        }
        return `Configuración #${config.id}`;
    };

    const sortedConfigs = [...configs].sort((a, b) => a.id - b.id);
    const totalPages = Math.ceil(sortedConfigs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedConfigs = sortedConfigs.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null) return <Loading text="Cargando configuración de correspondencia..." />;

    if (error) {
        return (
            <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg text-red-600">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div data-admin-page="true" className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 admin-page-title">Gestión de <span className="admin-title-accent">Configuración de Correspondencia</span></h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/config-correspondencia" className="text-[#c1348b] hover:text-[#8f1f64]">
                            Configuración de Correspondencia
                        </a>
                    </div>
                </div>

                <div className="flex gap-2 items-center">
                    <button
                        onClick={handleRefresh}
                        className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center h-[39px] text-sm font-medium transition-colors"
                        title="Refrescar lista"
                        disabled={loading}
                    >
                        <History className="w-5 h-5" />
                    </button>
                    {perm.can_create && (
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center h-[39px] text-sm font-medium transition-colors min-w-[170px]"
                            disabled={loading}
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Agregar Configuración
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />

                {isModalOpen && (
                    <AddCorrespondencia
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        onCreated={handleConfigCreated}
                    />
                )}

                {isEditModalOpen && selectedConfig && (
                    <EditCorrespondencia
                        isOpen={isEditModalOpen}
                        onClose={() => setIsEditModalOpen(false)}
                        onUpdated={handleConfigUpdated}
                        configId={selectedConfig.id}
                    />
                )}

                {isDeleteModalOpen && selectedConfig && (
                    <DeleteCorrespondencia
                        isOpen={isDeleteModalOpen}
                        onClose={() => setIsDeleteModalOpen(false)}
                        onDeleted={handleConfigDeleted}
                        configId={selectedConfig.id}
                        configName={getNombreCompletoParaModal(selectedConfig)}
                    />
                )}

                <div className="overflow-x-auto">
                    <AdminTable className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Plan - Servicio Principal
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Descripción
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Fecha Creación
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Estado
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <Notebook className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                Cargando configuraciones...
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : displayedConfigs.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <Notebook className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                No hay configuraciones registradas
                                            </span>
                                            {perm.can_create && (
                                                <p className="text-gray-500 text-sm">
                                                    Agrega una nueva configuración para comenzar
                                                </p>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                displayedConfigs.map((config) => (
                                    <tr key={config.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4">
                                            <div className="text-sm text-gray-900 font-medium">
                                                {getPlanServicioCompleto(config)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-500">
                                            {config.descripcion || 'Sin descripción'}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-500">
                                            {formatDate(config.fecha_creacion)}
                                        </td>
                                        <td className="px-4 py-4">
                                            <button
                                                onClick={() => handleToggleStatus(config.id, config.estado)}
                                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${config.estado
                                                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                                    : 'bg-red-100 text-red-800 hover:bg-red-200'
                                                    }`}
                                            >
                                                {config.estado ? 'Activo' : 'Inactivo'}
                                            </button>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex gap-2">
                                                {perm.can_update && (
                                                    <button
                                                        className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-[#c1348b] hover:text-[#8f1f64] transition"
                                                        title="Editar configuración"
                                                        onClick={() => openEditModal(config)}
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {perm.can_delete && (
                                                    <button
                                                        onClick={() => openDeleteModal(config)}
                                                        className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-800 transition"
                                                        title="Eliminar configuración"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </AdminTable>
                </div>

                {perm.can_read && !loading && sortedConfigs.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={sortedConfigs.length}
                    />
                )}
            </div>
        </div>
    );
};

export default ConfigCorrespondenciaPage;
